import { useState } from 'react'

import MailOpenIcon from '@heroicons/react/outline/MailOpenIcon'
import { Link } from 'react-router-dom'

import { ActionStatus } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T } from '@tribeplatform/react-components/i18n'
import { useSendResetPasswordEmail } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { EmptyState } from '@tribeplatform/react-ui-kit/EmptyState'

export const ForgotPassword = () => {
  const { mutateAsync: sendResetPasswordEmail, isLoading } =
    useSendResetPasswordEmail()
  const [resetEmail, setResetEmail] = useState('')

  if (resetEmail) {
    return (
      <div className="sm:mx-auto sm:w-full sm:max-w-md p-5 py-12">
        <EmptyState
          icon={<MailOpenIcon />}
          title={
            <T
              defaultMessage="Check your email"
              id="Auth.ForgotPass.CheckEmail"
            />
          }
          description={
            <>
              <p className="mt-5 text-center text-sm text-content-on-background-subdued">
                <T
                  defaultMessage="If we find an account associated with the email address, we’ve sent a temporary link to reset your password."
                  id="Auth.ForgotPass.CheckEmail.Notice.AccountExistence"
                />
              </p>
              <p className="mt-2 text-center text-sm text-content-on-background-subdued">
                <T
                  defaultMessage="Please check your inbox at {email}."
                  id="Auth.ForgotPass.CheckEmail.Notice.Inbox"
                  values={{
                    email: <strong>{resetEmail}</strong>,
                  }}
                />
              </p>
            </>
          }
        >
          <Button variant="outline" as={Link} to="/auth/login" size="lg">
            <T
              defaultMessage="Back to login"
              id="Auth.ForgotPass.CheckEmail.Actions.BackToLogin"
            />
          </Button>
        </EmptyState>
      </div>
    )
  }

  const onSubmit = async (data: { email: string }) => {
    const res = await sendResetPasswordEmail({ email: data.email })
    if (res.status === ActionStatus.succeeded) {
      setResetEmail(data.email)
    }
  }
  return (
    <>
      <div className="lg:w-1/2 md:w-full flex mx-auto flex-col py-8 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-content-on-background">
            <T
              defaultMessage="Forgot your password?"
              id="Generics.ForgotYourPassword"
            />
          </h2>
          <p className="mt-3 text-lg text-center text-content-on-background-subdued">
            <T
              defaultMessage="Enter your email below and we will{br}send you a link to reset it."
              id="Auth.ForgotPass.Instruct"
            />
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <Card className="py-8 px-4">
            <Form className="space-y-5" onSubmit={onSubmit}>
              <Form.Input
                autoComplete="email"
                label={<T defaultMessage="Email" id="Generics.Email" />}
                name="email"
                autoFocus
              />
              <div>
                <Button
                  fullWidth
                  variant="primary"
                  type="submit"
                  loading={isLoading}
                  size="lg"
                >
                  <T
                    defaultMessage="Send reset link"
                    id="Auth.ForgotPass.Actions.SendLink"
                  />
                </Button>
              </div>
            </Form>
          </Card>
        </div>
      </div>
    </>
  )
}
